import { FC, memo, useEffect, useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { useMediaQuery } from 'react-responsive';
import { styled } from '../../shared/styles';
import LazyImage from '../LazyImage';
import { UsersApiService } from '@api/users/users.api.service';
import { updateUser } from '@store/user';
import setLanguage from '@shared/i18n/setLanguage';
import { Language } from '@magnetmlm/common';
import { usePersistLocaleCookie } from '@entities/user/lib/hooks';
import { useUser } from '@hooks';

const languageOptions: { code: Language; flag: string }[] = [
  { code: Language.ENGLISH, flag: '/images/icons/flags/en.svg' },
  { code: Language.RUSSIAN, flag: '/images/icons/flags/ru.svg' },
  { code: Language.HINDI, flag: '/images/icons/flags/hi.svg' },
  { code: Language.FRENCH, flag: '/images/icons/flags/fr.svg' },
  { code: Language.CHINESE, flag: '/images/icons/flags/cn.svg' },
  { code: Language.INDONESIAN, flag: '/images/icons/flags/id.svg' },
  { code: Language.PORTUGUESE, flag: '/images/icons/flags/pt.svg' },
  { code: Language.SERBIAN, flag: '/images/icons/flags/sr.svg' },
  { code: Language.VIETNAMESE, flag: '/images/icons/flags/vi.svg' }
  // { code: 'cn', flag: '/images/icons/landing/cn.png' }
];

let LanguagePicker: FC<{ className?: string }> = ({ className }) => {
  const t = useTranslations();
  const lang = useLocale();
  const [isOpen, setIsOpen] = useState(false);
  const isMobile = useMediaQuery({ query: '(max-width: 567px)' });
  const user = useUser();

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const savedLanguage = localStorage.getItem('app-language') || 'en';
  }, []);

  // const handleChangeLanguage = (langCode: string) => {
  //   i18n.changeLanguage(langCode);
  //   localStorage.setItem('app-language', langCode);
  //   setlanguageOptions.find(({code}) => code === lang)(languageOptions.find(item => item.code === langCode)!);
  //   setIsOpen(false);
  // };

  const handleChangeLanguage = async (lang: Language) => {
    setLanguage(lang);

    if (user) {
      const res = await UsersApiService.changeLanguage({ language: lang });

      if (res) {
        updateUser({ lang });
      }
    }
    setIsOpen(false);
  };

  const toggleDropdown = () => setIsOpen(!isOpen);

  return (
    <div className={className}>
      <button
        className={`languageButton ${languageOptions.find(({ code }) => code === lang)?.code?.toLowerCase() === 'cn' && 'lang_img_cn'}`}
        onClick={toggleDropdown}
      >
        <LazyImage
          src={languageOptions.find(({ code }) => code === lang)?.flag || ''}
          alt={`Flag ${languageOptions.find(({ code }) => code === lang)?.flag}`}
        />
        {languageOptions.find(({ code }) => code === lang)?.code?.toUpperCase()}
      </button>
      {isMobile ? (
        // <Dialog open={isOpen}>

        // </Dialog>
        <div
          style={{ '--items-length': languageOptions.length } as React.CSSProperties}
          className={`languageList ${isOpen ? 'opened' : 'not_opened'}`}
        >
          {' '}
          <div className='language_title'>
            <span>{t('header.selectLang')}</span>
          </div>
          <div onClick={() => setIsOpen(false)} className='language_close'>
            <LazyImage src={'/images/icons/landing/close.svg'} alt='close' />
          </div>
          <div className='language_list_wrapper'>
            {languageOptions
              .filter(item => item.code !== languageOptions.find(({ code }) => code === lang)?.code)
              .map(lang => (
                <div
                  style={{ color: 'white' }}
                  className={`language_item ${lang.code.toLowerCase() === 'cn' && 'lang_img_cn'}`}
                  key={lang.code}
                  onClick={() => handleChangeLanguage(lang.code)}
                >
                  <LazyImage width={24} height={24} className='lang_img' src={lang.flag} alt='lang.flag' />
                  {lang.code.toUpperCase()}
                </div>
              ))}
          </div>
        </div>
      ) : (
        <div
          style={{ '--items-length': languageOptions.length } as React.CSSProperties}
          className={`languageList ${isOpen ? 'opened' : 'not_opened'}`}
        >
          <div className='language_title'>
            <span>{t('header.selectLang')}</span>
          </div>
          <div onClick={() => setIsOpen(false)} className='language_close'>
            <LazyImage src={'/images/icons/landing/close.svg'} alt='close' />
          </div>
          <div className='language_list_wrapper'>
            {languageOptions
              .filter(item => item.code !== languageOptions.find(({ code }) => code === lang)?.code)
              .map(lang => (
                <div
                  style={{ color: 'white' }}
                  className={`language_item ${lang.code.toLowerCase() === 'cn' && 'lang_img_cn'}`}
                  key={lang.code}
                  onClick={() => handleChangeLanguage(lang.code)}
                >
                  <LazyImage src={lang.flag} alt={lang.code} /> {lang.code.toUpperCase()}
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

LanguagePicker = styled(LanguagePicker, {
  '& *': {
    zIndex: 1111
  },
  '&': {
    position: 'relative',
    display: 'inline-flex',

    '@toDesktop': {
      marginLeft: 'auto'
    },
    '@to576': {
      // marginTop: 'auto',
      background: 'rgba(0, 0, 0, 0.3)',
      width: '100%',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '16.5px',
      borderRadius: '100px',
      marginTop: '12px'
    },
    img: {
      width: '25px',
      height: '25px'
    },

    '& .languageButton': {
      cursor: 'pointer',
      display: 'flex',
      fontSize: '18px',
      alignItems: 'center',
      fontWeight: 600,
      gap: '8px',
      padding: '6px 14px',
      background: 'rgba(255, 255, 255, 0.3)',
      color: 'white',
      border: 'solid 1px #3e3952',
      borderRadius: '12px',
      width: '86px',

      '@toXL': {
        fontSize: '18px'
      },
      img: {
        width: '24px',
        height: '18px'
      }
    }
  },
  '& .languageList .language_item': {
    fontWeight: 600,
    background: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    fontSize: '18px !important',
    gap: '8px',

    img: {
      width: '24px',
      height: '18px'
    },
    '@toXL': {
      fontSize: '18px'
    },
    '@to576': {
      padding: '16.5px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%',
      backgroundColor: 'rgba(0, 0, 0, 0.3)',

      borderRadius: '16px'
    }
  },
  '& .languageList': {
    position: 'absolute',
    top: 'calc(100% + 10px)',
    left: 0,
    // background: 'white',
    listStyle: 'none',
    padding: '6px 14px',
    margin: 0,
    background: 'rgba(71, 69, 69, 0.9)',
    border: '1px solid #3e3952',
    borderRadius: '12px',
    maxHeight: '1px',
    overflow: 'hidden',
    transition: '0.5s ease-in-out',
    opacity: 0,
    visibility: 'hidden',

    '@to576': {
      // display: none,
      backgroundColor: '#1c1929',
      position: 'fixed',
      width: '90vw',
      // top: 'auto',
      borderRadius: '16px',
      padding: '25px 20px',
      gap: '16px',
      // zIndex: 21,
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      zIndex: 1111
    },

    '& .language_list_wrapper': {
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',

      '@to576': {
        gap: '16px',
        maxHeight: '356px',
        overflow: 'auto'
      }
    },

    // transform: scaleY(0),

    '.language_item': {
      fontWeight: 600,
      background: 'none',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      fontSize: '18px !important',
      gap: '8px',
      lineHeight: 'normal !important',

      img: {
        width: '24px',
        height: '18px'
      },
      '@toXL': {
        fontSize: '18px'
      },
      '@to576': {
        lineHeight: 'normal !important',
        padding: '16.5px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.3)',
        fontSize: '18px !important',
        borderRadius: '16px',
        transition: '0.2s all'
      }
    }
  },
  '& .language_close': {
    display: 'none !important',
    '@to576': {
      padding: '0 !important',
      background: 'none !important',
      marginBottom: '13px',
      position: 'absolute',
      // width: 'fit-content',
      // height: 'fit-content',
      top: '20px',
      right: '20px',
      display: 'inline-block !important',
      width: '24px',
      maxWidth: '24px',
      height: '24px',

      img: {
        minWidth: '24px',
        minHeight: '24px'
      }
    }
  },

  '& .lang_img': {
    width: '24px',
    height: '24px',
    maxWidth: '24px',
    maxHeight: '24px'
  },
  '& .lang_img_cn': {
    // img: {
    //   width: '19px !important',
    //   maxWidth: '19px',
    //   marginLeft: '3px',
    //   height: '18px'
    // }
  },
  '& .languageList.opened': {
    maxHeight: 'calc(var(--items-length) * 40px + 20px)',
    opacity: 1,
    visibility: 'visible',
    '@to576': {
      maxHeight: 'max-content'
    }
  },
  '& .languageList.not_opened': {
    opacity: 0,
    visibility: 'hidden',
    maxHeight: '0px',
    transition: '0.3s ease-in-out',
    '@to576': {
      maxHeight: 'max-content'
    }
  },
  '& .language_title': {
    display: 'none !important',
    '@to576': {
      padding: '0 !important',
      background: 'none !important',
      display: 'flex !important',
      marginBottom: '20px',
      justifyContent: 'center',

      span: {
        textAlign: 'center !important',
        fontSize: '24px',
        fontWeight: 600,
        color: 'white'
      }
    }
  }
});

export default memo(LanguagePicker);
